import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth.store'
import { authService } from '@/modules/auth/services/auth.service'
import { LockScreen } from '@/modules/auth/lock/LockScreen'
import { getLastActivity, listenForOtherTabs, markActivity, useLockStore } from '@/modules/auth/lock/idle-lock'

const ACTIVITY_EVENTS = ['pointerdown', 'mousemove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const
const CHECK_EVERY_MS = 10_000

/**
 * Watches real input and locks after the role/device idle minutes. Counts
 * only time with NO input — any tap, key, scroll or mouse move (in any tab)
 * restarts the clock. Renders the lock screen while locked.
 */
export function IdleLockGate() {
  const security = useAuthStore((s) => s.session?.user.security)
  const locked = useLockStore((s) => s.locked)
  const idleMinutes = security?.idleMinutes ?? 0
  const queryClient = useQueryClient()
  const wasLocked = useRef(locked)

  // Just unlocked: reload anything that failed while locked (safety net —
  // locked calls are normally held and replayed by the API client).
  useEffect(() => {
    if (wasLocked.current && !locked) {
      void queryClient.refetchQueries({ type: 'active', predicate: (q) => q.state.status === 'error' })
    }
    wasLocked.current = locked
  }, [locked, queryClient])

  // Server says this session is already locked (reload while locked).
  useEffect(() => {
    if (security?.locked) useLockStore.getState().lock({ broadcast: false })
  }, [security?.locked])

  useEffect(() => listenForOtherTabs(), [])

  useEffect(() => {
    const onActivity = () => {
      if (!useLockStore.getState().locked) markActivity()
    }
    const options = { passive: true, capture: true } as const
    for (const name of ACTIVITY_EVENTS) window.addEventListener(name, onActivity, options)
    return () => {
      for (const name of ACTIVITY_EVENTS) window.removeEventListener(name, onActivity, options)
    }
  }, [])

  useEffect(() => {
    if (!idleMinutes) return
    const check = () => {
      if (useLockStore.getState().locked) return
      if (Date.now() - getLastActivity() >= idleMinutes * 60_000) {
        useLockStore.getState().lock()
        void authService.lock('idle').catch(() => undefined)
      }
    }
    check()
    const timer = window.setInterval(check, CHECK_EVERY_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') check()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [idleMinutes])

  return locked ? <LockScreen /> : null
}
